import {
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Ref,
} from 'vue'
import {
  FAIR_HOME_STICKY_COLLAPSE_LOCK_MS,
  FAIR_HOME_STICKY_PIN_GRACE_MS,
  canCommitFairHomeStickyCollapse,
  canFairHomeStickyChrome,
  resolveFairHomeStickyMegaCollapsed,
  shouldFairHomeStickyPin,
} from './fair-home-sticky.data'

/**
 * Page-level sticky chrome: pin dark header + mega when chips leave,
 * collapse expandable row on scroll-down (with grace after handoff).
 *
 * Collapse uses pending intent + dissolve lock so rapid direction changes
 * and mid-transition height measures cannot flip-flop the chrome.
 */
export function useFairHomeStickyChrome(
  menuEl: Ref<HTMLElement | null | undefined>,
) {
  const viewportWidth = ref(
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  )
  const isPinned = ref(false)
  const searchOpen = ref(false)
  const megaCollapsed = ref(false)
  /** Sticky header mega dropdown open — blocks scroll-down collapse. */
  const megaPanelOpen = ref(false)
  const lastScrollY = ref(
    typeof window !== 'undefined' ? window.scrollY : 0,
  )
  const pinGraceUntilMs = ref(0)

  /** Latest scroll-direction intent; applied when unlock allows. */
  let pendingCollapsed = false
  let lockUntilMs = 0
  let unlockTimer = 0
  let raf = 0

  const enabled = () => canFairHomeStickyChrome(viewportWidth.value)

  function now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now()
  }

  function clearUnlockTimer() {
    if (!unlockTimer) return
    window.clearTimeout(unlockTimer)
    unlockTimer = 0
  }

  function syncScrollBaseline() {
    if (typeof window === 'undefined') return
    lastScrollY.value = window.scrollY
  }

  function commitCollapsed(next: boolean, atMs: number) {
    if (!isPinned.value) {
      megaCollapsed.value = false
      pendingCollapsed = false
      return
    }
    if (megaCollapsed.value === next) return

    megaCollapsed.value = next
    lockUntilMs = atMs + FAIR_HOME_STICKY_COLLAPSE_LOCK_MS
    clearUnlockTimer()
    unlockTimer = window.setTimeout(() => {
      unlockTimer = 0
      // Drop layout-induced scroll delta from height settle.
      syncScrollBaseline()
      flushPendingCollapsed()
    }, FAIR_HOME_STICKY_COLLAPSE_LOCK_MS)
  }

  function flushPendingCollapsed() {
    const atMs = now()
    if (
      !canCommitFairHomeStickyCollapse({
        pendingCollapsed,
        currentCollapsed: megaCollapsed.value,
        nowMs: atMs,
        lockUntilMs,
      })
    ) {
      return
    }
    commitCollapsed(pendingCollapsed, atMs)
  }

  function resetCollapseState() {
    clearUnlockTimer()
    pendingCollapsed = false
    megaCollapsed.value = false
    lockUntilMs = 0
    pinGraceUntilMs.value = 0
  }

  function setMegaPanelOpen(open: boolean) {
    megaPanelOpen.value = open
    if (!open || !isPinned.value) return
    pendingCollapsed = false
    const atMs = now()
    lockUntilMs = 0
    clearUnlockTimer()
    commitCollapsed(false, atMs)
  }

  function measure() {
    const el = menuEl.value
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0
    const scrollDelta = scrollY - lastScrollY.value
    const atMs = now()

    if (!el) {
      isPinned.value = false
      searchOpen.value = false
      megaPanelOpen.value = false
      resetCollapseState()
      lastScrollY.value = scrollY
      return
    }

    const top = el.getBoundingClientRect().top
    const nextPinned = shouldFairHomeStickyPin(top, enabled(), isPinned.value)

    if (!nextPinned) {
      searchOpen.value = false
      megaPanelOpen.value = false
      resetCollapseState()
    } else if (!isPinned.value) {
      pendingCollapsed = false
      megaCollapsed.value = false
      lockUntilMs = 0
      clearUnlockTimer()
      pinGraceUntilMs.value = atMs + FAIR_HOME_STICKY_PIN_GRACE_MS
    } else {
      pendingCollapsed = resolveFairHomeStickyMegaCollapsed({
        isPinned: true,
        scrollDelta,
        currentCollapsed: pendingCollapsed,
        nowMs: atMs,
        pinGraceUntilMs: pinGraceUntilMs.value,
        megaPanelOpen: megaPanelOpen.value,
      })

      if (
        canCommitFairHomeStickyCollapse({
          pendingCollapsed,
          currentCollapsed: megaCollapsed.value,
          nowMs: atMs,
          lockUntilMs,
        })
      ) {
        commitCollapsed(pendingCollapsed, atMs)
      }
    }

    isPinned.value = nextPinned
    lastScrollY.value = scrollY
  }

  function handleResize() {
    viewportWidth.value = window.innerWidth
    measure()
  }

  function handleScroll() {
    if (raf) return
    raf = window.requestAnimationFrame(() => {
      raf = 0
      measure()
    })
  }

  onMounted(() => {
    lastScrollY.value = window.scrollY
    handleResize()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
    clearUnlockTimer()
    if (raf) window.cancelAnimationFrame(raf)
  })

  watch(menuEl, () => measure())

  watch(searchOpen, (open) => {
    if (!open || !isPinned.value) return
    pendingCollapsed = false
    const atMs = now()
    // Opening search should reveal the expandable row immediately.
    lockUntilMs = 0
    clearUnlockTimer()
    commitCollapsed(false, atMs)
  })

  return {
    isPinned,
    searchOpen,
    megaCollapsed,
    megaPanelOpen,
    setMegaPanelOpen,
    enabled,
    measure,
  }
}
